import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import securityApi from '../api/securityApi';
import { clearGuestScans, hasReturnPath, listGuestScans, removeGuestScans, saveGuestScan } from '../utils/guestScans';

// Create the context
const ScanContext = createContext();

// Create a provider component
export const ScanProvider = ({ children }) => {
  const [repoUrl, setRepoUrl] = useState("");
  const [scanId, setScanId] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanLogs, setScanLogs] = useState([]);
  const [scanStage, setScanStage] = useState('initializing');
  const [discoveredVulnCount, setDiscoveredVulnCount] = useState(0);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [expandedVulnerability, setExpandedVulnerability] = useState(null);
  const [fixingVulns, setFixingVulns] = useState([]);
  const [fixProgressMap, setFixProgressMap] = useState({});
  const [fixLogsMap, setFixLogsMap] = useState({});
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [authChecking, setAuthChecking] = useState(true); // Add loading state for auth check
  // Add compliance-related state
  const [complianceQuestion, setComplianceQuestion] = useState('');
  const [complianceAnswer, setComplianceAnswer] = useState(null);
  const [complianceLoading, setComplianceLoading] = useState(false);
  const [complianceError, setComplianceError] = useState(null);
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);
  const [questionHistory, setQuestionHistory] = useState([]);
  // Add dashboard-related state
  const [dashboardStats, setDashboardStats] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState(null);
  const [latestFindings, setLatestFindings] = useState([]);
  // New state for repository management
  const [repositories, setRepositories] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [repoScans, setRepoScans] = useState([]);
  const [repoStats, setRepoStats] = useState(null);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [loadingRepoDetails, setLoadingRepoDetails] = useState(false);
  const [currentScanId, setCurrentScanId] = useState(null); 
  const [retryPromptedVulns, setRetryPromptedVulns] = useState([]);
  const [scaAutofixEnabled, setScaAutofixEnabled] = useState(false);
  const [queuePosition, setQueuePosition] = useState(null);

  const scanStageRef = useRef(scanStage);
  useEffect(() => { scanStageRef.current = scanStage; }, [scanStage]);
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  // Pagination state
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 25,
    offset: 0,
    page: 1,
    totalPages: 0
  });

  const [counts, setCounts] = useState({
    total: 0,
    fixed: 0,
    by_severity: {
      critical: 0,
      high: 0,
      medium: 0,
      low: 0
    },
    by_category: {
      code_security: 0,
      third_party_dependencies: 0,
      secret_detection: 0
    }
  });

  const [filters, setFilters] = useState({
    category: null,
    severity: null,
    status: null
  });

  const [sortConfig, setSortConfig] = useState({
    by: 'severity',
    order: 'desc'
  });

  // Attach scans started in this browser before logging in to the account. Tokens the
  // backend did not accept (expired, already claimed elsewhere) are kept, so their links
  // keep working; the backend revokes the token of every scan it does claim.
  const claimGuestScans = async () => {
    const guestScans = listGuestScans();
    if (guestScans.length === 0) return;
    try {
      const { claimed = [] } = await securityApi.claimAnonymousScans(guestScans);
      removeGuestScans(claimed);
    } catch (error) {
      console.error("Could not attach guest scans to the account:", error);
    }
  };

  // Add effect to check authentication status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        setAuthChecking(true);
        const userData = await securityApi.getCurrentUser();
        setUser(userData);
        if (userData && hasReturnPath()) {
          // Login started from a guest scan's results: it must be the user's before that
          // page loads again, or the page would offer fixes the backend still refuses.
          await claimGuestScans();
        } else if (userData) {
          claimGuestScans(); // not awaited: it must not hold up the loading screen
        }
      } catch (error) {
        console.error("Auth check failed:", error);
        setUser(null);
      } finally {
        setAuthChecking(false);
      }
    };

    checkAuth();
  }, []);

  // Sample scanning logs for the UI animation
  const scanningLogs = [
    { icon: '🔍', message: 'Initializing security analysis...' },
    { icon: '📊', message: 'Detecting frameworks and dependencies...' },
    { icon: '🔎', message: 'Identified: Node.js, Express, React, MongoDB' },
    { icon: '📚', message: 'Loading security patterns database...' },
    { icon: '🧪', message: 'Scanning authentication flows...' },
    { icon: '⚠️', message: 'Alert: Weak password policy detected' },
    { icon: '🔒', message: 'Analyzing data access patterns...' },
    { icon: '❗', message: 'Critical: SQL injection vulnerability found' },
    { icon: '📝', message: 'Examining input validation...' },
    { icon: '⚠️', message: 'Alert: XSS vulnerability detected in user comments' },
    { icon: '🔑', message: 'Checking authentication mechanisms...' },
    { icon: '⚠️', message: 'Alert: Insecure JWT implementation' },
    { icon: '🔐', message: 'Scanning for secrets and credentials...' },
    { icon: '⚠️', message: 'Alert: Exposed API keys in source code' },
    { icon: '📈', message: 'Generating security report...' },
    { icon: '✅', message: 'Security analysis complete' }
  ];

  // Start the repository scan
  const startScan = async (url, isPrivate = false, installationId = null) => {
    try {
      setScanProgress(0);
      setScanLogs([]);
      setError(null);
      setRepoUrl(url);
      setDiscoveredVulnCount(0);
      setQueuePosition(null);
      setScanStage(null);

      // Connect to repository and start scan using our Python backend. Without a login only
      // public repositories can be scanned, and the scan is read with its access token.
      let response;
      if (user) {
        response = await securityApi.connectRepository(url, isPrivate, installationId);
      } else {
        response = await securityApi.startAnonymousScan(url);
        saveGuestScan({ scanId: response.scanId, token: response.accessToken, repoUrl: response.repoUrl || url });
      }
      setScanId(response.scanId);
      // Only now: the scan form stays up while the scan is being started, so a refusal
      // (quota, private repository, ...) shows on it instead of being lost when it remounts.
      setAnalyzing(true);

      // Handle queued response from backend
      if (response.status === 'queued') {
        setScanStage('queued');
        setQueuePosition(response.queuePosition != null ? response.queuePosition : null);
      }

      // Set up polling to get scan progress updates from backend
      let lastLogId = 0;
      let pollingAttempts = 0;
      let consecutiveErrors = 0;
      const MAX_POLLING_ATTEMPTS = 450; // 450 * 2s = 15 minutes max
      const MAX_CONSECUTIVE_ERRORS = 5; // Stop after 5 consecutive errors

      const scanInterval = setInterval(async () => {
        pollingAttempts++;

        // Check if we've exceeded maximum polling time
        if (pollingAttempts > MAX_POLLING_ATTEMPTS) {
          clearInterval(scanInterval);
          setError('Scan is taking longer than expected (15 minutes). The scan may still be running in the background. Please check the results page later or contact support if the issue persists.');
          setAnalyzing(false);
          return;
        }

        try {
          // Get progress updates from the backend
          const progressData = await securityApi.getScanProgress(response.scanId, lastLogId);

          // Reset error counter on successful request
          consecutiveErrors = 0;

          // Handle queued state
          if (progressData.status === 'queued') {
            setScanStage('queued');
            setQueuePosition(progressData.queuePosition != null ? progressData.queuePosition : null);
            return;
          }
          // Transition out of queued state
          if (scanStageRef.current === 'queued') {
            setScanStage('scanning');
            setQueuePosition(null);
          }

          setScanProgress(progressData.progress);

          // Update discovered vulnerability count if available
          if (progressData.vulnCount !== undefined) {
            setDiscoveredVulnCount(progressData.vulnCount);
          }

          // Add new logs if available
          if (progressData.logs && progressData.logs.length > 0) {
            setScanLogs(prev => [...prev, ...progressData.logs]);
            lastLogId = progressData.logCount || lastLogId + progressData.logs.length;
          }

          // Show partial results if many vulnerabilities found (Point 4: Early pagination)
          if (progressData.vulnCount > 50 && progressData.progress > 50 && vulnerabilities.length === 0) {
            // Fetch partial results to show user progress
            try {
              const partialResults = await securityApi.getScanResults(response.scanId, { limit: 25, offset: 0 });
              if (partialResults.vulnerabilities && partialResults.vulnerabilities.length > 0) {
                setVulnerabilities(partialResults.vulnerabilities.map(normalizeVuln));
                if (partialResults.pagination) setPagination(partialResults.pagination);
                if (partialResults.counts) setCounts(partialResults.counts);
              }
            } catch (partialError) {
              console.log('Could not fetch partial results, will wait for completion:', partialError);
            }
          }

          // If scan is complete, fetch results and clear interval
          if (progressData.progress >= 100 || progressData.status === 'complete') {
            clearInterval(scanInterval);
            setTimeout(() => {
              fetchScanResults(response.scanId);
            }, 1000);
          }
        } catch (error) {
          consecutiveErrors++;
          console.error(`Error polling scan progress (attempt ${consecutiveErrors}/${MAX_CONSECUTIVE_ERRORS}):`, error);

          // Only stop polling after multiple consecutive errors
          if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
            clearInterval(scanInterval);
            const errorType = error.code === 'ECONNABORTED' || error.message.includes('timeout')
              ? 'Connection timeout'
              : 'Network error';
            setError(`${errorType} after ${MAX_CONSECUTIVE_ERRORS} failed attempts. The scan may still be running. Please refresh the page or check the results page in a few minutes.`);
            setAnalyzing(false);
          }
          // Otherwise, continue polling - might be temporary network issue
          // The scan continues in the background
        }
      }, 2000); // Poll every 2 seconds

      return () => clearInterval(scanInterval);
    } catch (err) {
      const detail = err.response && err.response.data && err.response.data.detail;
      setError('Failed to start scan: ' + (typeof detail === 'string' ? detail : err.message));
      setAnalyzing(false);
    }
  };

  // Normalize a single vulnerability from the API into the shape VulnerabilityItem expects.
  // The backend stores two schemas in repository_vulnerabilities.details:
  //   - Full Trivy shape: Title, Description, file_path, line_number, Severity, impact, ...
  //   - Minimal shape:    message, file_path, line_number (no Title/Description)
  // VulnerabilityItem destructures: title, description, file, line, severity, impact, ...
  const normalizeVuln = (vuln) => {
    const remediation = Array.isArray(vuln.remediation)
      ? vuln.remediation
      : typeof vuln.remediation === 'object' && vuln.remediation !== null
        ? vuln.remediation
        : vuln.remediation
          ? [vuln.remediation]
          : [];

    return {
      ...vuln,
      // Map PascalCase API fields → lowercase component props
      title: vuln.title || vuln.Title || vuln.message || '',
      description: vuln.description || vuln.Description || vuln.message || '',
      file: vuln.file || vuln.file_path || '',
      line: vuln.line ?? vuln.line_number ?? null,
      severity: (vuln.severity || vuln.Severity || '').toLowerCase() || null,
      remediation,
    };
  };

  // Fetch scan results from API
  const fetchScanResults = async (id, retryCount = 0) => {
    const MAX_RETRIES = 30; // 30 * 2s = 60 seconds max wait for results to be ready

    try {
      // Get the real scan results from our Python backend
      const results = await securityApi.getScanResults(id);

      if (results.status === 'complete') {
        const normalizedVulns = (results.vulnerabilities || []).map(normalizeVuln);
        setVulnerabilities(normalizedVulns);
        setScaAutofixEnabled(!!results.scaAutofixEnabled);

        // Update pagination and counts from API response
        if (results.pagination) {
          console.log('[FetchScanResults] Setting pagination:', results.pagination);
          setPagination(results.pagination);
        }

        if (results.counts) {
          console.log('[FetchScanResults] Setting counts:', results.counts);
          setCounts(results.counts);
        }

        setAnalyzing(false);
      } else if (retryCount < MAX_RETRIES) {
        // If the scan isn't complete yet, wait and try again
        setTimeout(() => fetchScanResults(id, retryCount + 1), 2000);
      } else {
        // Max retries exceeded
        setError('Results are taking longer than expected to process. Please refresh the page or navigate to the results page directly.');
        setAnalyzing(false);
      }
    } catch (err) {
      // Better error messages for timeout errors
      if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        setError('Request timeout while fetching results. The scan may have completed - try navigating to the results page directly.');
      } else {
        setError('Failed to fetch scan results: ' + err.message);
      }
      setAnalyzing(false);
    }
  };

  // Toggle vulnerability expansion
  const toggleVulnerability = (id) => {
    if (expandedVulnerability === id) {
      setExpandedVulnerability(null);
    } else {
      setExpandedVulnerability(id);
    }
  };

  // Fix a vulnerability
  const fixVulnerability = async (id) => {
    try {
      setFixingVulns(prev => [...prev, id]);
      setFixProgressMap(prev => ({ ...prev, [id]: 0 }));
      setFixLogsMap(prev => ({ ...prev, [id]: [] }));
      // Remove from retryPromptedVulns if retrying
      setRetryPromptedVulns(prev => prev.filter(v => v !== id));
      await securityApi.applyFix(scanId, id);
      let lastLogId = 0;
      let hasPromptedRetry = false;
      const fixInterval = setInterval(async () => {
        try {
          const progressData = await securityApi.getFixProgress(scanId, id, lastLogId);

          // Handle queued state for fix
          if (progressData.status === 'queued') {
            setFixProgressMap(prev => ({ ...prev, [id]: 0 }));
            if (progressData.queuePosition) {
              setFixLogsMap(prev => ({
                ...prev,
                [id]: [{ icon: '⏳', message: `Queued - position #${progressData.queuePosition}` }],
              }));
            }
            return;
          }

          setFixProgressMap(prev => ({ ...prev, [id]: progressData.progress }));
          if (progressData.logs && progressData.logs.length > 0) {
            setFixLogsMap(prev => ({
              ...prev,
              [id]: [...(prev[id] || []), ...progressData.logs],
            }));
            lastLogId = progressData.logCount || lastLogId + progressData.logs.length;
          }
          if (progressData.status === 'complete' || progressData.status === 'error' || progressData.status === 'failed' || progressData.progress >= 100) {
            clearInterval(fixInterval);

            // Handle failed status (impossible scenarios like peer dependency conflicts)
            if (progressData.status === 'failed') {
              // Extract the last log message as the failure reason
              const failureReason = progressData.logs?.length > 0
                ? progressData.logs[progressData.logs.length - 1].message
                : 'Automatic fix not possible';

              // Update vulnerability with fix_failed status and reason
              setVulnerabilities(prev =>
                prev.map(vuln => {
                  if (vuln.id !== id) return vuln;
                  return {
                    ...vuln,
                    status: 'fix_failed',
                    fixError: failureReason,
                  };
                })
              );
              setFixingVulns(prev => prev.filter(v => v !== id));
              return;
            }

            if (progressData.status === 'error') {
              setFixingVulns(prev => prev.filter(v => v !== id));
              // Only prompt if not already prompted for this vuln
              if (!retryPromptedVulns.includes(id) && !hasPromptedRetry) {
                hasPromptedRetry = true;
                setRetryPromptedVulns(prev => [...prev, id]);
                const wantsRetry = window.confirm('⚠️  Auto-fix failed. Would you like to try again?');
                if (wantsRetry) {
                  setTimeout(() => {
                    fixVulnerability(id);
                  }, 500);
                  // Remove from prompted list so it can be retried if fails again
                  setRetryPromptedVulns(prev => prev.filter(v => v !== id));
                }
              }
              return;
            }
            if (progressData.status === 'complete' || progressData.progress >= 100) {
              // Update the vulnerability with the fix results directly from progressData
              // This avoids pagination issues where the fixed vulnerability might not be in the first page
              setVulnerabilities(prev =>
                prev.map(vuln => {
                  if (vuln.id !== id) return vuln;

                  // Build the updated vulnerability object
                  const updatedVuln = {
                    ...vuln,
                    status: 'fixed',
                    prUrl: progressData.prUrl || vuln.prUrl,
                    prNumber: progressData.prNumber || vuln.prNumber,
                  };

                  // Update remediation if available in progressData
                  if (progressData.remediation !== undefined) {
                    // Normalize remediation to preserve format
                    if (Array.isArray(progressData.remediation)) {
                      updatedVuln.remediation = progressData.remediation;
                    } else if (typeof progressData.remediation === 'object' && progressData.remediation !== null) {
                      updatedVuln.remediation = progressData.remediation;  // Preserve object format for multi_agent_ai
                    } else if (progressData.remediation) {
                      updatedVuln.remediation = [progressData.remediation];  // Wrap strings in array
                    } else {
                      updatedVuln.remediation = [];
                    }
                  }

                  return updatedVuln;
                })
              );
              setFixingVulns(prev => prev.filter(v => v !== id));
            }
          }
        } catch (error) {
          console.error('Error polling fix progress:', error);
          // Always clear interval on error
          clearInterval(fixInterval);
        }
      }, 2000);
      return () => clearInterval(fixInterval);
    } catch (err) {
      setError('Failed to start fix process: ' + err.message);
      setFixingVulns(prev => prev.filter(v => v !== id));
    }
  };

  // Generate a security report
  const generateReport = async (id, options = {}) => {
    try {
      const reportScanId = id || scanId;
      if (!reportScanId) throw new Error('No active scan');

      const result = await securityApi.generateReport(reportScanId, options);
      return result;
    } catch (err) {
      setError('Failed to generate report: ' + err.message);
      return null;
    }
  };

  // Calculate the security score based on vulnerabilities
  const calculateSecurityScore = () => {
    const totalVulns = vulnerabilities.length;
    if (totalVulns === 0) return 100;

    const fixedVulns = vulnerabilities.filter(v => v.status === 'fixed').length;
    const criticalVulns = vulnerabilities.filter(v => v.severity === 'critical' && v.status === 'open').length;
    const highVulns = vulnerabilities.filter(v => v.severity === 'high' && v.status === 'open').length;

    // Base score of 100, minus penalties for open vulnerabilities
    let score = 100;
    score -= criticalVulns * 15;
    score -= highVulns * 10;
    score -= (totalVulns - fixedVulns - criticalVulns - highVulns) * 5;

    return Math.max(0, score);
  };

  // Reset the scan state to go back to the welcome screen
  const resetScan = () => {
    setRepoUrl("");
    setScanId(null);
    setAnalyzing(false);
    setScanProgress(0);
    setScanLogs([]);
    setVulnerabilities([]);
    setError(null);
  };

  const handleLoginSuccess = async (userData) => {
    setUser(userData);
    if (userData) await claimGuestScans();
  };

  // New function to load compliance data
  // Remove the duplicate useEffect for compliance data
  // and modify the loadComplianceData function
  const loadComplianceData = async () => {
    if (!repoUrl) {
      setComplianceError('No repository connected. Please connect to a repository first.');
      return;
    }

    if (complianceLoading) return; // Prevent multiple simultaneous calls

    setComplianceLoading(true);
    setComplianceError(null);

    try {
      // Load suggested questions
      const suggestedResponse = await securityApi.getSuggestedQuestions(repoUrl);
      // Transform the questions objects into an array of strings
      const formattedQuestions = Array.isArray(suggestedResponse.questions)
        ? suggestedResponse.questions.map(q => typeof q === 'string' ? q : q.question)
        : [];
      setSuggestedQuestions(formattedQuestions);

      // Load question history
      const historyResponse = await securityApi.getQuestionHistory(repoUrl);
      setQuestionHistory(Array.isArray(historyResponse.history) ? historyResponse.history : []);
    } catch (err) {
      console.error('Error loading compliance data:', err);
      setComplianceError(`Failed to load compliance data: ${err.message || 'Unknown error'}`);
      setSuggestedQuestions([]);
      setQuestionHistory([]);
    } finally {
      setComplianceLoading(false);
    }
  };

  // Update the askComplianceQuestion function
  const askComplianceQuestion = async (question) => {
    if (!repoUrl) {
      setComplianceError('No repository connected. Please connect to a repository first.');
      return null;
    }

    if (complianceLoading) return null; // Prevent multiple simultaneous calls

    try {
      setComplianceLoading(true);
      setComplianceError(null);

      const response = await securityApi.askComplianceQuestion(question, repoUrl);

      if (response && response.answer) {
        setComplianceAnswer({
          question: question,
          answer: response.answer,
          timestamp: new Date().toISOString()
        });

        // Load updated history after successful question
        await loadComplianceData();

        return response;
      } else {
        throw new Error("Invalid response format from server");
      }
    } catch (err) {
      console.error('Error asking compliance question:', err);
      setComplianceError(`Failed to get answer: ${err.message || 'Unknown error'}`);
      return null;
    } finally {
      setComplianceLoading(false);
    }
  };

  // Add dashboard data loading function
  const loadDashboardData = async () => {
    try {
      setDashboardLoading(true);
      setDashboardError(null);

      // Fetch dashboard stats
      const stats = await securityApi.getDashboardStats();
      setDashboardStats(stats);

      // Fetch latest findings
      const findings = await securityApi.getLatestFindings();
      setLatestFindings(findings.findings || []);

      setDashboardLoading(false);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setDashboardError(`Failed to load dashboard data: ${err.message || 'Unknown error'}`);
      setDashboardLoading(false);
    }
  };

  // Add function to navigate to dashboard
  const goToDashboard = async () => {
    await loadDashboardData();
  };

  const goToRepositories = () => {
    // future logic here
  };

  const logout = async () => {
    try {
      await securityApi.logout();
    } catch (error) {
      console.error("Logout failed:", error);
    }
    // Leave no guest access tokens behind for the next person on this browser.
    clearGuestScans();
  };

  // Load repositories
  const loadRepositories = useCallback(async () => {
    setLoadingRepos(true);
    try {
      const data = await securityApi.getRepositories();
      // Sort repositories by latest_scan_time in descending order (newest first)
      const sortedData = [...data].sort((a, b) => {
        const dateA = a.latest_scan_time ? new Date(a.latest_scan_time) : new Date(0);
        const dateB = b.latest_scan_time ? new Date(b.latest_scan_time) : new Date(0);
        return dateB - dateA;
      });
      setRepositories(sortedData);
      setError(null);
    } catch (err) {
      setError(`Failed to load repositories: ${err.message}`);
      setRepositories([]);
    } finally {
      setLoadingRepos(false);
    }
  }, []);

  // Select repository and load its details
  const selectRepository = useCallback(async (repoUrl) => {
    setLoadingRepoDetails(true);
    setRepoScans([]);
    setRepoStats(null);
    try {
      // Find the full repository object from the loaded repositories
      const fullRepo = repositories.find(repo => repo.repo_url === repoUrl);
      setSelectedRepo(fullRepo);

      const scans = await securityApi.getRepositoryScans(repoUrl);
      // Sort scans by start_time in descending order (newest first)
      const sortedScans = [...scans].sort((a, b) => 
        new Date(b.start_time) - new Date(a.start_time)
      );
      setRepoScans(sortedScans);

      const stats = await securityApi.getRepositoryStats(repoUrl);
      setRepoStats(stats);
      setError(null);
    } catch (err) {
      setError(`Failed to load repository details: ${err.message}`);
      setRepoScans([]);
      setRepoStats(null);
    } finally {
      setLoadingRepoDetails(false);
    }
  }, [repositories]);

  const loadScan = useCallback(async (id, options = {}) => {
    if (!id) return;
    try {
      setAnalyzing(true);
      setError(null);

      const data = await securityApi.getScanResults(id, {
        limit: pagination.limit,
        offset: 0, // Reset to first page on new load
        ...filters,
        sortBy: sortConfig.by,
        sortOrder: sortConfig.order,
        ...options
      });

      if (data) {
        if (data.status === 'complete') {
          setScanId(id);
          setRepoUrl(data.repoUrl || '');
          setVulnerabilities((data.vulnerabilities || []).map(normalizeVuln));

          if (data.pagination) {
            console.log('[LoadScan] Setting pagination:', data.pagination);
            setPagination(data.pagination);
          } else {
            console.warn('[LoadScan] No pagination data in response');
          }

          if (data.counts) {
            console.log('[LoadScan] Setting counts:', data.counts);
            setCounts(data.counts);
          } else {
            console.warn('[LoadScan] No counts data in response');
          }

          setScaAutofixEnabled(data.scaAutofixEnabled || false);
          // Scan is fully loaded — stop the loading state.
          setAnalyzing(false);
        } else {
          // Scan not yet complete (e.g. "finalising", "scanning").
          // Don't set scanId — that would lock the useEffect guard and prevent
          // ResultsPage from retrying once the scan finishes.
          // Don't call setAnalyzing(false) here — fetchScanResults owns the
          // loading state and will clear it when polling finishes.
          if (data.repoUrl) setRepoUrl(data.repoUrl);
          fetchScanResults(id);
        }
      } else {
        // No data returned — nothing to poll, stop loading.
        setAnalyzing(false);
      }
    } catch (err) {
      setAnalyzing(false);
      // Rethrow the error to be handled by the component
      throw err;
    }
  }, [pagination.limit, filters.category, filters.severity, filters.status, sortConfig.by, sortConfig.order]);

  // Pagination helper functions
  const changePage = async (pageNumber) => {
    const newOffset = (pageNumber - 1) * pagination.limit;

    try {
      setAnalyzing(true);
      const data = await securityApi.getScanResults(scanId, {
        limit: pagination.limit,
        offset: newOffset,
        ...filters,
        sortBy: sortConfig.by,
        sortOrder: sortConfig.order
      });

      setVulnerabilities((data.vulnerabilities || []).map(normalizeVuln));
      if (data.pagination) {
        setPagination(data.pagination);
      }
      if (data.counts) {
        setCounts(data.counts);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const changePageSize = async (newLimit) => {
    setPagination(prev => ({ ...prev, limit: newLimit }));

    try {
      setAnalyzing(true);
      const data = await securityApi.getScanResults(scanId, {
        limit: newLimit,
        offset: 0, // Reset to first page
        ...filters,
        sortBy: sortConfig.by,
        sortOrder: sortConfig.order
      });

      setVulnerabilities((data.vulnerabilities || []).map(normalizeVuln));
      if (data.pagination) {
        setPagination(data.pagination);
      }
      if (data.counts) {
        setCounts(data.counts);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const applyFilter = async (filterType, value) => {
    const newFilters = { ...filters, [filterType]: value };
    setFilters(newFilters);

    try {
      setAnalyzing(true);
      const data = await securityApi.getScanResults(scanId, {
        limit: pagination.limit,
        offset: 0, // Reset to first page when filtering
        ...newFilters,
        sortBy: sortConfig.by,
        sortOrder: sortConfig.order
      });

      setVulnerabilities((data.vulnerabilities || []).map(normalizeVuln));
      if (data.pagination) {
        setPagination(data.pagination);
      }
      if (data.counts) {
        setCounts(data.counts);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const clearFilters = async () => {
    setFilters({ category: null, severity: null, status: null });

    try {
      setAnalyzing(true);
      const data = await securityApi.getScanResults(scanId, {
        limit: pagination.limit,
        offset: 0,
        sortBy: sortConfig.by,
        sortOrder: sortConfig.order
      });

      setVulnerabilities((data.vulnerabilities || []).map(normalizeVuln));
      if (data.pagination) {
        setPagination(data.pagination);
      }
      if (data.counts) {
        setCounts(data.counts);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setAnalyzing(false);
    }
  };

  // Value object to be provided to consumers
  const value = {
    repoUrl,
    setRepoUrl,
    scanId,
    analyzing,
    scanProgress,
    scanLogs,
    scanStage,
    setScanStage,
    discoveredVulnCount,
    vulnerabilities,
    setVulnerabilities,
    expandedVulnerability,
    setExpandedVulnerability,
    fixingVulns,
    setFixingVulns,
    fixProgressMap,
    setFixProgressMap,
    fixLogsMap,
    setFixLogsMap,
    error,
    setError,
    user,
    setUser,
    authChecking,
    isGuest: !authChecking && !user,
    startScan,
    fetchScanResults,
    toggleVulnerability,
    fixVulnerability,
    calculateSecurityScore,
    generateReport,
    resetScan,
    logout,
    claimGuestScans,
    // Compliance-related values
    complianceQuestion,
    setComplianceQuestion,
    complianceAnswer,
    complianceLoading,
    complianceError,
    suggestedQuestions,
    questionHistory,
    askComplianceQuestion,
    loadComplianceData,
    // Dashboard-related values
    dashboardStats,
    dashboardLoading,
    dashboardError,
    latestFindings,
    loadDashboardData,
    goToDashboard,
    // New repository management values
    repositories,
    selectedRepo,
    repoScans,
    repoStats,
    loadingRepos,
    loadingRepoDetails,
    loadRepositories,
    selectRepository,
    currentScanId, // Keep this to track scan in progress
    handleLoginSuccess,
    // New repo functions
    goToRepositories,
    loadScan,
    scaAutofixEnabled,
    queuePosition,
    scanStage,
    // Pagination values
    pagination,
    counts,
    filters,
    sortConfig,
    changePage,
    changePageSize,
    applyFilter,
    clearFilters,
  };

  return (
    <ScanContext.Provider value={value}>
      {children}
    </ScanContext.Provider>
  );
};

// Custom hook to use the scan context
export const useScan = () => {
  const context = useContext(ScanContext);
  if (context === undefined) {
    throw new Error('useScan must be used within a ScanProvider');
  }
  return context;
};

export default ScanContext;
